import { Component, effect, inject, OnDestroy, OnInit, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { VoiceLoadDialogComponent } from 'projects/shared/src/lib/components/voice-load-dialog/voice-load-dialog.component';
import { HeaderComponent } from './components/header/header.component';
import { ENVIRONMENT_TOKEN } from 'projects/shared/src/lib/core/injection-tokens/environment.token';
import { AuthStore } from 'projects/shared/src/lib/store/auth.store';
import {
  ApplicationIdentificator,
  isApplicationMatch,
  SettingsStore,
} from 'projects/shared/src/lib/store/settings.store';
import { detectIsTablet } from 'projects/shared/src/lib/core/helpers/user-agent-utils';
import { NavigationService } from 'projects/shared/src/lib/core/services/navigation.service';
import { SpeechService } from 'projects/shared/src/lib/core/services/speech.service';
import { LanguageService } from 'projects/shared/src/lib/core/services/language.service';
import { SignalRService } from 'projects/shared/src/lib/core/services/signalr.service';
import { RouterCommonPagesEnum } from 'projects/shared/src/lib/infrastructure/enums/common-pages.enum';
import { RouterAuthEnum } from 'projects/shared/src/lib/infrastructure/enums/router-link.enum';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-root',
  imports: [FormsModule, RouterOutlet, HeaderComponent, VoiceLoadDialogComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
  public showHeader = signal<boolean>(true);

  private readonly environment = inject(ENVIRONMENT_TOKEN);
  private readonly authStore = inject(AuthStore);
  private readonly settingsStore = inject(SettingsStore);
  private readonly navigationService = inject(NavigationService);
  private readonly speechService = inject(SpeechService);
  private readonly languageService = inject(LanguageService);
  private readonly signalRService = inject(SignalRService);
  private readonly translate = inject(TranslateService);
  public readonly router = inject(Router);
  public readonly route = inject(ActivatedRoute);

  private readonly destroy$ = new Subject<void>();

  private readonly languageSetting = this.settingsStore.language;

  constructor() {
    effect(() => {
      if (!this.settingsStore.isLoaded()) {
        return;
      }

      var langSetting = this.languageSetting();
      const currentLang = untracked(() => this.translate.currentLang());
      if (langSetting && langSetting !== currentLang) {
        this.translate.use(langSetting);
      }
    });
  }

  async ngOnInit(): Promise<void> {
    this.languageService.initialize();
    await this.authStore.refreshAuthInfo();
    if (this.authStore.isAuthenticated()) {
      await this.initUser();
      this.navigateToHomePage();
    } else {
      await this.authStore.authorize();
      if (this.authStore.isAuthenticated()) {
        await this.initUser();
        this.navigateToHomePage();
      } else {
        this.authStore.logoutLocal();
        await this.authStore.authorize();
      }
    }

    this.route.url.subscribe(([url]) => {
      if (window.location.pathname.startsWith(`/${RouterAuthEnum.LoggedOut}`))
        this.showHeader.set(false);
    });

    await this.speechService.initalize().catch((err) => {
      console.error('Error initializing speech service: ', err);
    });
  }

  async forceLogout() {
    const duarationEndpoint = `${this.environment.baseApiUrl}/auth/token/revocation`;
    const body = JSON.stringify({
      spaClientId: this.environment.identityClientId,
      tokens: [
        { token: this.authStore.authInfo().accessToken, tokenTypeHint: 'access_token' },
        { token: this.authStore.authInfo().refreshToken, tokenTypeHint: 'refresh_token' },
      ],
    });
    try {
      const authInfo = this.authStore.authInfo();
      this.authStore.logoutLocal();
      fetch(duarationEndpoint, {
        method: 'POST',
        body,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authInfo.accessToken}`,
        },
        keepalive: true,
      });
    } catch (e) {
      console.error(`Failed to send revocation request: ${e}`);
    }
  }

  private navigateToHomePage(): void {
    if (window.location.pathname.includes(`/${RouterCommonPagesEnum.ContentPlayerTestPage}`))
      return;
    this.navigationService.navigateToHomePage();
  }

  private async initUser() {
    const account = await this.authStore.requestAccount();
    this.languageService.initialize(account?.identityId);
    await this.settingsStore.loadSettings(ApplicationIdentificator.ATR);
    await this.applyTabletNumpadDefault();

    if (account?.identityId) {
      await this.initSignalRConnection(account.identityId);
    }
  }

  /**
   * On a tablet the student has no keyboard of their own, so a test taken with
   * the numpad off is not representative. Re-applied on every settings change
   * rather than once, so the numpad cannot disappear mid-test.
   */
  private async applyTabletNumpadDefault(): Promise<void> {
    if (!detectIsTablet()) return;

    try {
      await this.settingsStore.ensureNumpadEnabled(ApplicationIdentificator.ATR);
    } catch (error) {
      console.error('[ATR] Failed to persist tablet numpad default:', error);
    }
  }

  /**
   * Initializes the SignalR connection for receiving real-time settings updates.
   */
  private async initSignalRConnection(userIdentityId: string): Promise<void> {
    // Subscribe BEFORE starting the connection so a push that arrives during the
    // connect handshake can't slip through the gap (settingsUpdated$/reconnected$
    // are plain Subjects with no replay).
    this.signalRService.settingsUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (settings) => {
        if (isApplicationMatch(settings.application, ApplicationIdentificator.ATR)) {
          if (!this.environment.production)
            console.log('[ATR] Applying pushed settings update:', settings);
          this.settingsStore.applyPushedSettings(settings);
          await this.applyTabletNumpadDefault();
        }
      });

    // After a reconnect, refetch settings to reconcile any pushes lost while
    // disconnected (SignalR is fire-and-forget; the Azure SignalR Service does not buffer).
    this.signalRService.reconnected$
      .pipe(takeUntil(this.destroy$))
      .subscribe(async () => {
        if (!this.environment.production)
          console.log('[ATR] Reconnected - refetching settings to reconcile');
        await this.settingsStore.loadSettings(ApplicationIdentificator.ATR);
        await this.applyTabletNumpadDefault();
      });

    try {
      await this.signalRService.startConnection(userIdentityId);
    } catch (error) {
      console.error('[ATR] Failed to initialize SignalR connection:', error);
      // Non-critical failure - the app can still work without real-time updates
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.signalRService.stopConnection();
  }
}
